/* eslint-disable jsx-a11y/anchor-is-valid */
import secondaryComponents from "../../../../../../components/secondaryComponents";
import { motion } from "framer-motion";
import globalStyles from "../../../../../../assets/styles/base/globalPage.module.scss";
import primaryComponents, {
  BackButton,
} from "../../../../../../components/primaryComponents";
import {
  extractReadableDateOnly,
  formatCurrency,
} from "../../../../../../helpers";
import {
  POExtra,
  POItemsTable,
  POPricingUpdate,
  POShareDialog,
  UpdateSuplierForm,
} from "../../../../../../components/secondaryComponents/PurchaseOrder";
import { useEditPurchaseOrder } from "../EditPurchaseOrder/useEditPurchaseOrder";
import { Link, useNavigate } from "react-router-dom";
import { pathConstants } from "../../../../../../routes/pathContants";
import { usePurchaseOrderDetails } from "./usePurchaseOrderDetails";
import {
  RoutedModal,
  WarningDialog,
} from "../../../../../../components/common";
import { useGetPricingTypes } from "../../../../../../hooks/api/queries";
import { useStoreListObject } from "../../../../../../hooks/useStoreListObject";
import { EditIcon } from "../../../../../../assets/svgIcons";

export const PurchaseOrderDetails = () => {
  const navigate = useNavigate();
  const { isLoading, PODetails, POItems } = useEditPurchaseOrder();
  const {
    isUpdatingStatus,
    views,
    search,
    setViews,
    handleApproveOrRejectPO,
    closeNewPODialog,
  } = usePurchaseOrderDetails();

  const { data: pricingTypes = [], isLoading: isLoadingPricingTypes } =
    useGetPricingTypes(search.get("new_approved_po_id") !== null);
  const { storeMap } = useStoreListObject();
  const currencyCode = storeMap?.[POItems?.[0]?.store_id]?.currency_code;

  return (
    <secondaryComponents.MetaSetter
      title="Lumi Merchant | Purchase Order Details"
      description="View the details of a purchase order"
    >
      <secondaryComponents.RequestLoaderTrigger>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`${globalStyles.pageColumnGaps}`}
        >
          <div>
            <BackButton />
          </div>

          <secondaryComponents.LoaderHelper
            classNames={"mt-5"}
            isLoading={isLoading}
          >
            <div>
              <div className={`${globalStyles.pageHeaders} mb-3`}>
                <div>
                  <h3 className="font-weight-normal">
                    <span className="gray--text">Purchase Order No.: </span>
                    {PODetails?.po_number || "N/A"}
                  </h3>
                </div>
                <div
                  className="d-flex flex-wrap ml-auto"
                  style={{ gap: "10px" }}
                >
                  {PODetails?.status === "IN_REVIEW" ? (
                    <>
                      <primaryComponents.Button
                        classNames={"btn btn--primary"}
                        style={{ width: "140px" }}
                        onClick={() => {
                          setViews((prev) => ({
                            ...prev,
                            isAcceptDialog: true,
                          }));
                        }}
                      >
                        Approve PO
                      </primaryComponents.Button>
                      <primaryComponents.Button
                        classNames={"btn btn--outline-red "}
                        style={{ width: "140px" }}
                        onClick={() => {
                          setViews((prev) => ({
                            ...prev,
                            isRejectDialog: true,
                          }));
                        }}
                      >
                        Reject PO
                      </primaryComponents.Button>
                    </>
                  ) : null}

                  <secondaryComponents.NavigateWithPermission
                    permissionParentKey={"Inventory Management"}
                    permissionChildKey={"can_create_or_update_purchase_order"}
                  >
                    <primaryComponents.Button
                      classNames={"btn btn--outline"}
                      style={{ width: "140px" }}
                      onClick={() =>
                        navigate(
                          pathConstants.INVENTORY_PURCHASE_ORDER_EDIT({
                            id: PODetails?.id,
                          }),
                        )
                      }
                    >
                      Update PO
                    </primaryComponents.Button>
                  </secondaryComponents.NavigateWithPermission>

                  {PODetails?.status === "ACCEPTED" ? (
                    <secondaryComponents.NavigateWithPermission
                      permissionParentKey={"Inventory Management"}
                      permissionChildKey={"can_create_or_update_goods_received"}
                    >
                      <primaryComponents.Button
                        classNames={"btn btn--success"}
                        style={{ width: "140px" }}
                        onClick={() =>
                          navigate(
                            pathConstants.INVENTORY_GOODS_RECEIVED_CREATE +
                              `?po_id=${PODetails?.id}&po_number=${PODetails?.po_number}`,
                          )
                        }
                      >
                        Receive Goods
                      </primaryComponents.Button>
                    </secondaryComponents.NavigateWithPermission>
                  ) : null}

                  <div className="ml-auto">
                    <POExtra PODetails={PODetails} />
                  </div>
                </div>
              </div>

              <div className={globalStyles.gridDetailsView}>
                <div>
                  <p className="dark--text">Status</p>
                  <p
                    className={`${globalStyles[PODetails?.status === "IN_REVIEW" ? "statusBlock--pending" : PODetails?.status === "ACCEPTED" ? "statusBlock--success" : PODetails?.status === "REJECTED" ? "statusBlock--failed" : "statusBlock"]}`}
                  >
                    {PODetails?.status?.replace("_", " ") || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="dark--text">Created by</p>
                  <p className="small-text-size">
                    {PODetails?.initiated_by?.first_name || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="dark--text">PO Date</p>
                  <p className="small-text-size">
                    {PODetails?.po_date
                      ? extractReadableDateOnly(PODetails?.po_date)
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="dark--text">Supplier</p>

                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "10px" }}
                  >
                    <Link
                      to={pathConstants.INVENTORY_SUPPLIERS_DETAILS({
                        id: PODetails?.supplier?.id,
                      })}
                      className="purple--text"
                    >
                      {PODetails?.supplier?.name || "N/A"}
                    </Link>

                    {PODetails?.status === "ACCEPTED" && (
                      <secondaryComponents.ToolTipV2 tipText="Update Supplier">
                        <span
                          className="mr-1"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            setViews((prev) => ({ ...prev, isSupplier: true }))
                          }
                        >
                          <EditIcon />
                        </span>
                      </secondaryComponents.ToolTipV2>
                    )}
                  </div>
                </div>
                <div>
                  <p className="dark--text">Amount</p>
                  <p className="small-text-size">
                    {formatCurrency({
                      value: PODetails?.total_item_amount,
                      currencyCode: currencyCode,
                    })}
                  </p>
                </div>

                <div>
                  <p className="dark--text">Store</p>
                  <p className="small-text-size">
                    {PODetails?.stores?.map((store) => store?.name)?.join(", ")}
                  </p>
                </div>
                <div>
                  <p className="dark--text">Approved By</p>
                  <p className="small-text-size">
                    {PODetails?.approved_by?.first_name || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="dark--text">Rejected By</p>
                  <p className="small-text-size">
                    {PODetails?.rejected_by?.first_name || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h5>Order Items</h5>

              <div
                className={`${globalStyles.cardWithBackground} white`}
                style={{ gap: "30px" }}
              >
                <POItemsTable POItems={POItems} PODetails={PODetails} />

                <div>
                  <h4 className="font-weight-normal mb-3">Price Summary</h4>
                  <div className="d-flex justify-content-between">
                    <p>Total</p>
                    <h5>
                      {formatCurrency({
                        value: PODetails?.total_item_amount,
                        currencyCode: currencyCode,
                      })}
                    </h5>
                  </div>
                </div>

                <div>
                  <h4 className="font-weight-normal mb-3">
                    Additional Details
                  </h4>
                  <div
                    className={"d-flex flex-column flex-md-row"}
                    style={{ gap: "15px" }}
                  >
                    <div
                      className={`d-flex flex-column w-100 ${globalStyles.cardWithBorder}`}
                      style={{ gap: "25px" }}
                    >
                      <h6 className="gray--text ">Shipping Details</h6>
                      <div>
                        <p className="small-text-size">Name</p>
                        <h5 className="font-weight-semibold">
                          {PODetails?.shipping_details?.name || "N/A"}
                        </h5>
                      </div>
                      <div>
                        <p className="small-text-size">Phone Number</p>
                        <h5 className="font-weight-semibold">
                          {PODetails?.shipping_details?.phone_code || ""}{" "}
                          {PODetails?.shipping_details?.phone_number || "N/A"}
                        </h5>
                      </div>
                      <div>
                        <p className="small-text-size">Address</p>
                        <h5 className="font-weight-semibold">
                          {PODetails?.shipping_details?.address || "N/A"}
                        </h5>
                      </div>
                      <div>
                        <p className="small-text-size">Delivery Date</p>
                        <h5 className="font-weight-semibold">
                          {PODetails?.shipping_details?.delivery_date
                            ? extractReadableDateOnly(
                                PODetails?.shipping_details?.delivery_date,
                              )
                            : "N/A"}
                        </h5>
                      </div>
                    </div>
                    <div className={`w-100 ${globalStyles.cardWithBorder}`}>
                      <p>Comments or Special Instruction </p>
                      <h5 className="font-weight-semibold">
                        {PODetails?.notes || "N/A"}
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </secondaryComponents.LoaderHelper>

          {/* Approve Modal */}
          <secondaryComponents.Modal
            isActive={views?.isAcceptDialog}
            closeModal={() =>
              setViews((prev) => ({ ...prev, isAcceptDialog: false }))
            }
            width={500}
          >
            <WarningDialog
              title={`Approve PO: ${PODetails?.po_number}`}
              btnClassNames="btn--primary"
              closeModal={() =>
                setViews((prev) => ({ ...prev, isAcceptDialog: false }))
              }
              handler={() => handleApproveOrRejectPO(PODetails?.id, "ACCEPTED")}
              isLoading={isUpdatingStatus}
            >
              <p>
                Supplier:{" "}
                <span className="dark--text">{PODetails?.supplier?.name}</span>
              </p>
              <p>
                PO Date:{" "}
                <span className="dark--text">
                  {PODetails?.po_date
                    ? extractReadableDateOnly(PODetails?.po_date)
                    : "N/A"}
                </span>
              </p>
            </WarningDialog>
          </secondaryComponents.Modal>

          {/* Reject Modal */}
          <secondaryComponents.Modal
            isActive={views?.isRejectDialog}
            closeModal={() =>
              setViews((prev) => ({ ...prev, isRejectDialog: false }))
            }
            width={500}
          >
            <WarningDialog
              title={`Reject PO: ${PODetails?.po_number}`}
              closeModal={() =>
                setViews((prev) => ({ ...prev, isRejectDialog: false }))
              }
              handler={() => handleApproveOrRejectPO(PODetails?.id, "REJECTED")}
              isLoading={isUpdatingStatus}
            >
              <p>
                Supplier:{" "}
                <span className="dark--text">{PODetails?.supplier?.name}</span>
              </p>
              <p>
                PO Date:{" "}
                <span className="dark--text">
                  {PODetails?.po_date
                    ? extractReadableDateOnly(PODetails?.po_date)
                    : "N/A"}
                </span>
              </p>
            </WarningDialog>
          </secondaryComponents.Modal>

          {/* Update Selling Price Modal */}
          <RoutedModal
            isActive={search.get("new_approved_po_id") !== null}
            width={1400}
            headerTitle="Update Items Selling Pricing"
            closeModal={() => closeNewPODialog("new_approved_po_id")}
          >
            <secondaryComponents.LoaderHelper
              classNames={"mt-5"}
              isLoading={isLoading || isLoadingPricingTypes}
            >
              <POPricingUpdate
                PODetails={PODetails}
                POItems={POItems}
                pricingTypes={pricingTypes}
                closeModal={() => closeNewPODialog("new_approved_po_id")}
              />
            </secondaryComponents.LoaderHelper>
          </RoutedModal>

          {/* Share Modal */}
          <RoutedModal
            isActive={search.get("new_po_id") !== null}
            width={550}
            style={{ height: "auto" }}
            leftHeaderSide={
              <h5>What would you like to do with this newly created P.O?</h5>
            }
            closeModal={() => closeNewPODialog("new_po_id")}
          >
            <POShareDialog
              POId={search.get("new_po_id")}
              closeModal={() => closeNewPODialog("new_po_id")}
            />
          </RoutedModal>

          <RoutedModal
            isActive={views?.isSupplier}
            closeModal={() =>
              setViews((prev) => ({ ...prev, isSupplier: false }))
            }
            headerTitle="Update Supplier"
            style={{ height: "auto" }}
            width={500}
          >
            <UpdateSuplierForm
              po_id={PODetails?.id}
              supplier_id={PODetails?.supplier?.id}
              closeModal={() =>
                setViews((prev) => ({ ...prev, isSupplier: false }))
              }
            />
          </RoutedModal>
        </motion.div>
      </secondaryComponents.RequestLoaderTrigger>
    </secondaryComponents.MetaSetter>
  );
};
